import { ActionConfirmation, usePublishActionOutcome, useHasActionOutcomeHost, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useState } from "react";
import {
  registrationSettledSummary,
  registrationSettlementWarning,
  type RegistrationSettledSummary,
  type RegistrationSettlementWarning,
} from "../booking/registrationReconciliation.js";
import {
  staffBooking,
  type LessonBlockDetail,
  type LessonBlockProduct,
} from "../booking/staff.js";
import { TutorAddCredits } from "../booking/TutorAddCredits.js";
import { useAsync } from "../useAsync.js";

/**
 * Calendar entry point for the existing lightweight tutor block-registration
 * flow. The mutation itself stays in `TutorAddCredits`/staffBooking; this only
 * supplies the student already established by the appointment drawer and then
 * verifies the authoritative credit read before the drawer closes.
 */
export function DrawerAddCredits({
  studentId,
  studentName,
  providerId,
  serviceName,
  onBusyChange,
  onChanged,
  onCancel,
}: {
  studentId: string;
  studentName: string;
  providerId: number;
  serviceName?: string | null;
  onBusyChange: (busy: boolean) => void;
  onChanged: () => void;
  onCancel: () => void;
}) {
  const publish = usePublishActionOutcome();
  const hasHost = useHasActionOutcomeHost();
  const [result, setResult] = useState<ActionOutcome | null>(null);
  const [warning, setWarning] = useState<RegistrationSettlementWarning | null>(null);
  const [settledSummary, setSettledSummary] = useState<RegistrationSettledSummary | null>(null);
  const [recovery, setRecovery] = useState<(() => Promise<LessonBlockDetail>) | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const products = useAsync<LessonBlockProduct[]>(
    () => staffBooking.lessonBlockProducts(),
    [],
  );

  async function apply(
    mutation: () => Promise<LessonBlockDetail>,
  ): Promise<void> {
    onBusyChange(true);
    let accepted: LessonBlockDetail | null = null;
    try {
      const detail = await mutation();
      accepted = detail;
      const partial = registrationSettlementWarning(detail);
      const settled = registrationSettledSummary(detail, settledSummary);
      setWarning(partial);
      setSettledSummary((prior) => registrationSettledSummary(detail, prior));
      setRecovery(partial ? () => mutation : null);
      const credits = await staffBooking.lessonCredits(studentId);
      if (credits.portal_student_id !== studentId) {
        throw new Error("The refreshed credit response did not match this student.");
      }
      if (!Number.isFinite(credits.total_available_credits)) throw new Error("The refreshed credit balance was unavailable.");
      setBalance(credits.total_available_credits);
      const outcome: ActionOutcome = { variant: partial ? "attention" : "success", title: "Lesson block added", description: studentName,
        details: [{ value: "5 lesson credits registered" }, { label: "New balance", value: `${credits.total_available_credits} credits` },
          ...(settled ? [{ value: <><p>{settled.heading}</p><ul>{settled.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>{settled.more > 0 && <p>and {settled.more} more</p>}</> }] : []),
          ...(partial ? [{ value: partial.message }] : [])] };
      // Publish even when the receipt stays open for settlement review. The
      // drawer scope hands this result to the page on Close, Escape or backdrop.
      const hosted = publish(outcome);
      if (!partial && !settled) {
        if (!hosted) setResult(outcome);
        onChanged();
      }
    } catch (cause: unknown) {
      if (accepted) publish({ variant: "attention", title: "Lesson block added; balance needs checking", description: studentName,
        details: [{ value: "The block was registered, but the authoritative credits could not be refreshed. Do not add another block. Retry the existing registration to recover its result." }] });
      setRetryError(cause instanceof Error ? cause.message : "The authoritative credit state could not be re-read.");
      throw cause;
    } finally {
      onBusyChange(false);
    }
  }

  if (result) return <ActionConfirmation {...result} action={<button onClick={onChanged}>Done</button>} reveal />;

  if (settledSummary || (warning && recovery)) {
    const controls = <>
      {retryError && <p className="error">{retryError}</p>}
      {warning?.retryable && recovery && <button disabled={retryBusy} onClick={() => {
        setRetryBusy(true);
        setRetryError(null);
        void apply(recovery).catch((cause: unknown) => {
          setRetryError(cause instanceof Error ? cause.message : "Settlement could not be retried.");
        }).finally(() => setRetryBusy(false));
      }}>{retryBusy ? "Updating…" : "Retry existing block settlement"}</button>}
      <button className="quiet" disabled={retryBusy} onClick={onChanged}>Close</button>
    </>;
    if (hasHost) return <div>{controls}</div>;
    return <ActionConfirmation variant={warning ? "attention" : "success"} title="Lesson block added" description={studentName}
      details={[
        ...(balance !== null ? [{ label: "New balance", value: `${balance} credits` }] : []),
        ...(settledSummary ? [{ value: <><p><strong>{settledSummary.heading}</strong></p><ul>{settledSummary.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>{settledSummary.more > 0 && <p>and {settledSummary.more} more</p>}</> }] : []),
        ...(warning ? [{ value: warning.message }] : []),
      ]} action={controls} />;
  }

  if (products.loading) {
    return <><p className="empty">Loading configured lesson blocks…</p><button className="quiet" onClick={onCancel}>Cancel</button></>;
  }
  if (products.error) {
    return <><div className="error small">We could not load the configured 5-credit products right now.</div><button className="quiet" onClick={onCancel}>Cancel</button></>;
  }

  return (
    <TutorAddCredits
      studentId={studentId}
      studentName={studentName}
      ownProviderId={providerId}
      initialServiceName={serviceName}
      products={products.data ?? []}
      onCancel={onCancel}
      onApply={apply}
    />
  );
}
